import { Route, Routes } from 'react-router-dom'

import { Suspense, lazy } from 'react'

import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { DashboardLayout } from './components/layout/DashboardLayout'
import { usePlugins } from './contexts/PluginContext'
import {
  ActivityLog,
  AddProduct,
  Adminstrations,
  Analytics,
  Attributes,
  BlogDetail,
  BlogEditorPage,
  BlogList,
  Brands,
  CampaignBuilder,
  Campaigns,
  Categories,
  ChatSessions,
  Coupons,
  CreateOrder,
  CustomerDetail,
  CustomerReport,
  Customers,
  Dashboard,
  DeliveryMethods,
  DeliveryLocationTypes,
  EmailDashboard,
  IPBlocklist,
  InventoryDashboard,
  InventoryReport,
  Login,
  MediaLibrary,
  NotFound,
  NoteList,
  OrderDetail,
  OrderSources,
  Orders,
  PosOrders,
  PaymentGateways,
  PluginMarketplace,
  ProductDetail,
  Products,
  Profile,
  ReturnsDashboard,
  RevenueReport,
  ReviewsDashboard,
  RolesPage,
  SMSGateways,
  SalesReport,
  Settings,
  ShippingDashboard,
  TaxDashboard,
  Branches,
  CashHistory,
  CreditRepayments,
  MeasurementUnits,
  MembershipTiers,
  PriceBooks,
  ProfitLoss,
  PosReports,
  PosOffers,
  PosSettings,
  ShiftHistory,
  ShiftReports,
  Suppliers,
  Terminals,
} from './pages'
import { ThemeBuilderPage } from './pages/theme-management/ThemeBuilderPage'
import { SubscriptionPage } from '@/pages/subscription/SubscriptionPage'

export const AppRoutes = () => {
  const { getPluginRoutes } = usePlugins()
  const pluginRoutes = getPluginRoutes()

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />

      {/* Admin Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="products" element={<Products />} />
          <Route path="products/new" element={<AddProduct />} />
          <Route path="products/:id" element={<ProductDetail />} />
          <Route path="orders" element={<Orders />} />
          <Route path="orders/new" element={<CreateOrder />} />
          <Route path="orders/:id" element={<OrderDetail />} />
          <Route path="orders/sources" element={<OrderSources />} />
          <Route path="orders/methods" element={<DeliveryMethods />} />
          <Route path="orders/locations" element={<DeliveryLocationTypes />} />
          <Route path="orders/notes" element={<NoteList />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="coupons" element={<Coupons />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="reports/sales" element={<SalesReport />} />
          <Route path="reports/inventory" element={<InventoryReport />} />
          <Route path="reports/customers" element={<CustomerReport />} />
          <Route path="reports/revenue" element={<RevenueReport />} />
          <Route path="themes" element={<ThemeBuilderPage />} />
          <Route path="plugins" element={<PluginMarketplace />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/new" element={<CampaignBuilder />} />
          <Route path="campaigns/:id" element={<CampaignBuilder />} />
          <Route path="categories" element={<Categories />} />
          <Route path="brands" element={<Brands />} />
          <Route path="attributes" element={<Attributes />} />
          <Route path="inventory" element={<InventoryDashboard />} />
          <Route path="shipping" element={<ShippingDashboard />} />
          <Route path="taxes" element={<TaxDashboard />} />
          <Route path="media" element={<MediaLibrary />} />
          <Route path="reviews" element={<ReviewsDashboard />} />
          <Route path="marketing" element={<EmailDashboard />} />
          <Route path="returns" element={<ReturnsDashboard />} />
          <Route path="blog" element={<BlogList />} />
          <Route path="blog/new" element={<BlogEditorPage />} />
          <Route path="blog/:id" element={<BlogEditorPage />} />
          <Route path="blog/:id/preview" element={<BlogDetail />} />
          <Route path="system/activity-log" element={<ActivityLog />} />
          <Route path="system/chat-sessions" element={<ChatSessions />} />
          <Route path="system/sms-gateways" element={<SMSGateways />} />
          <Route path="system/payment-gateways" element={<PaymentGateways />} />
          <Route path="system/ip-blocklist" element={<IPBlocklist />} />
          <Route path="system/roles" element={<RolesPage />} />
          <Route path="system/administration" element={<Adminstrations />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="units" element={<MeasurementUnits />} />
          <Route path="pos/orders" element={<PosOrders />} />
          <Route path="pos/branches" element={<Branches />} />
          <Route path="pos/terminals" element={<Terminals />} />
          <Route path="pos/shifts" element={<ShiftHistory />} />
          <Route path="pos/cash" element={<CashHistory />} />
          <Route path="pos/credit-repayments" element={<CreditRepayments />} />
          <Route path="pos/shift-report" element={<ShiftReports />} />
          <Route path="pos/profit-loss" element={<ProfitLoss />} />
          <Route path="pos/reports" element={<PosReports />} />
          <Route path="pos/price-books" element={<PriceBooks />} />
          <Route path="pos/membership-tiers" element={<MembershipTiers />} />
          <Route path="pos/offers" element={<PosOffers />} />
          <Route path="pos/settings" element={<PosSettings />} />
          <Route path="settings" element={<Settings />} />
          <Route path="settings/subscription" element={<SubscriptionPage />} />
          <Route path="profile" element={<Profile />} />

          {/* Dynamic Plugin Routes */}
          {pluginRoutes.map((route) => {
            // Handle dynamic imports
            if (route.component) {
              const LazyComponent = lazy(route.component)
              return (
                <Route
                  key={route.path}
                  path={route.path.replace('/', '')}
                  element={
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center h-screen">Loading...</div>
                      }
                    >
                      <LazyComponent />
                    </Suspense>
                  }
                />
              )
            }
            // Handle pre-loaded components
            return (
              <Route key={route.path} path={route.path.replace('/', '')} element={route.element} />
            )
          })}

          {/* Catch-all: the 404 renders inside the shell (§3); signed-out visitors go to the login first */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  )
}
